<template>
  <div>
    <v-toolbar id="agenda" flat height="30" class="mt-1 mb-1">
      <strong class="text--secondary"> {{ $t("menu.agenda") }}: </strong>

      <v-spacer></v-spacer>
      <v-tooltip bottom v-if="$security.checkUserAccess('ViewMeetingTimer')">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            x-small
            class="mr-2"
            height="24"
            v-bind="attrs"
            v-on="on"
            outlined
            elevation="6"
            raised
            color="pageHeader"
            dark
            @click.stop="openMeetingTimer()"
            ><v-icon size="15">mdi-timer-sand</v-icon
            >{{ $t("agenda.btnMeetingTimer") }}</v-btn
          >
        </template>
        <span>{{ $t("agenda.tipMeetingTimer") }}</span>
      </v-tooltip>
      <v-tooltip bottom v-if="$security.checkUserAccess('CreateAgenda')">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            x-small
            height="24"
            outlined
            elevation="6"
            raised
            class="mr-2"
            v-bind="attrs"
            v-on="on"
            color="pageHeader"
            dark
            @click.stop="createAgenda()"
            ><v-icon size="15">mdi-plus</v-icon
            >{{ $t("agenda.btnCreateNewAgenda") }}</v-btn
          >
        </template>
        <span>{{ $t("agenda.tipCreateNewAgenda") }} </span>
      </v-tooltip>
      <agenda-topic-selector
        v-if="$security.checkUserAccess('UpdateMeeting')"
        :selectedMeeting="meetingObject"
        @onTopicSelection="handleTopicSelection"
      ></agenda-topic-selector>
      <v-tooltip bottom v-if="$security.checkUserAccess('DownloadConvocation')">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            x-small
            height="24"
            outlined
            elevation="6"
            raised
            class="mr-2"
            color="pageHeader"
            dark
            v-bind="attrs"
            v-on="on"
            @click.stop="downloadConvocation()"
            ><v-icon size="15">mdi-file-download</v-icon
            >{{ $t("agenda.btnDownloadConvo") }}</v-btn
          >
        </template>
        <span>{{ $t("agenda.tipDownloadConvo") }}</span>
      </v-tooltip>
      <v-tooltip bottom v-if="$security.checkUserAccess('OpenDocument')">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            x-small
            outlined
            height="24"
            elevation="6"
            raised
            class="mr-2"
            color="pageHeader"
            dark
            v-bind="attrs"
            v-on="on"
            @click.stop="OpenAllDocuments()"
            ><v-icon size="15">mdi-open-in-new</v-icon
            >{{ $t("agenda.OpenAllDocs") }}</v-btn
          >
        </template>
        <span>{{ $t("agenda.OpenAllDocs") }}</span>
      </v-tooltip>

      <v-tooltip bottom v-if="$security.checkUserAccess('OpenDocument')">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            x-small
            outlined
            height="24"
            elevation="6"
            raised
            class="mr-2"
            color="pageHeader"
            dark
            v-bind="attrs"
            v-on="on"
            @click.stop="SaveAllDocuments()"
            ><v-icon size="15">mdi-download-multiple</v-icon
            >{{ $t("agenda.SaveAllDocs") }}</v-btn
          >
        </template>
        <span>{{ $t("agenda.SaveAllDocs") }}</span>
      </v-tooltip>
    </v-toolbar>

    <v-card outlined elevation="2" min-width="1000px">
      <v-card-text>
        <div>
          <v-row class="agendaRow" style="border-bottom: none">
            <v-col cols="12" sm="3" md="3" lg="3" class="agendaColHeader">
              <h3>{{ $t("agenda.hAgenda&Topics") }}</h3>
            </v-col>
            <v-col cols="12" sm="1" md="1" lg="1" class="agendaColHeader">
              <h3>{{ $t("agenda.hPriority") }}</h3>
            </v-col>
            <v-col cols="12" sm="1" md="1" lg="1" class="agendaColHeader">
              <h3>{{ $t("agenda.hScheduledTime") }}</h3>
            </v-col>
            <v-col cols="12" sm="3" md="3" lg="3" class="agendaColHeader">
              <h3>{{ $t("agenda.hPresentor") }}</h3>
            </v-col>
            <v-col cols="12" sm="3" md="3" lg="3" class="agendaColHeader">
              <h3>{{ $t("agenda.hDocuments") }}</h3>
            </v-col>
          </v-row>

          <v-expansion-panels
            style="display: contents !important"
            tile
            flat
            multiple
            v-model="panel"
          >
            <draggable
              v-model="data.topicCategories"
              group="topicCategories"
              @change="onCatOrderChanged($event)"
              :disabled="!$security.checkUserAccess('UpdateMeeting')"
            >
              <!-- {{ categorizedTopics }} -->
              <v-expansion-panel
                class="pa-0"
                v-for="(category, parentIndex) in categorizedTopics"
                :key="category.value"
                expand
                v-model="panel"
              >
                <v-expansion-panel-header
                  v-if="category.topics.length > 0"
                  class="categoryHeader"
                  :style="
                    $security.checkUserAccess('UpdateMeeting')
                      ? `background-color:${category.color} !important;cursor: move !important;`
                      : `background-color:${category.color} !important;`
                  "
                >
                  <h4>
                    {{ category.name }}
                  </h4>
                </v-expansion-panel-header>
                <v-expansion-panel-content class="pa-0">
                  <draggable
                    v-model="category.topics"
                    :group="category.name"
                    @change="onChanged($event, category)"
                    :disabled="!$security.checkUserAccess('UpdateMeeting')"
                  >
                    <v-row
                      v-for="(item, index) in category.topics"
                      :key="item.Id"
                      :item="item"
                      :headers="topicHeads"
                      class="agendaRow"
                      :style="
                        $security.checkUserAccess('UpdateMeeting')
                          ? 'cursor: move;'
                          : ''
                      "
                      align="start"
                      justify="center"
                    >
                      <v-col cols="12" sm="3" md="3" lg="3" class="agendaCol">
                        <div
                          class="text-subtitle-2 pageHeader--text"
                          style="min-width: 300px; display: contents"
                        >
                          <strong v-if="item.agenda"
                            ><v-icon color="pageHeader" left
                              >mdi-view-agenda</v-icon
                            >{{ item.agenda.agendaName }}</strong
                          >
                        </div>
                        <div
                          style="padding-left: 10px"
                          class="text-body-2 mb-2"
                          v-if="item.agenda"
                        >
                          <ul>
                            <li
                              v-if="
                                item.agenda.topics.length > 1 ||
                                item.agenda.agendaName != item.topicName
                              "
                            >
                              {{ item.topicName }}
                            </li>
                            <li v-for="text in item.subTopics" :key="text">
                              {{ text }}
                            </li>
                          </ul>
                        </div>
                        <div
                          v-if="String(category.isapprovalrequired) === 'true'"
                        >
                          <approval-control
                            class="mt-3 ml-1 mb-2"
                            v-if="item.agenda && approvalStatus"
                            v-model="item.approval"
                            :isAttendee="isAttendee"
                            :agenda-name="item.agenda.agendaName"
                            :topic-name="item.topicName"
                            :meetingObj="meetingObject"
                            :voteData="getVote(item.Id)"
                            :topicId="item.Id"
                            @onApprovalChange="onApprovalChange(item)"
                            @onApprovalSettingChange="
                              onApprovalSettingChange(item)
                            "
                          ></approval-control>
                        </div>
                      </v-col>

                      <v-col cols="12" sm="1" md="1" lg="1" class="agendaCol">
                        <coloredChipTemplate
                          v-if="item.agenda"
                          :textValue="item.agenda.priority"
                          chipColumn="priority"
                        ></coloredChipTemplate>
                      </v-col>
                      <v-col cols="12" sm="1" md="1" lg="1" class="agendaCol">
                        <v-menu
                          :ref="`menuRef_${parentIndex}_${index}`"
                          bottom
                          offset-y
                          left
                          :close-on-content-click="false"
                          max-width="120"
                        >
                          <template v-slot:activator="{ on: menu, attrs }">
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on: tooltip }">
                                <v-chip
                                  style="padding: 0 5px"
                                  small
                                  color="secondary lighten-2"
                                  dark
                                  v-bind="attrs"
                                  v-on="{ ...tooltip, ...menu }"
                                  >{{ item.timeInMinutes }} min</v-chip
                                >
                              </template>
                              <span>
                                {{
                                  $security.checkUserAccess("UpdateMeeting")
                                    ? $t("agenda.tipUpdateTime")
                                    : $t("agenda.tipScheduleTime")
                                }}</span
                              >
                            </v-tooltip>
                          </template>
                          <v-card
                            flat
                            tile
                            v-if="$security.checkUserAccess('UpdateMeeting')"
                          >
                            <v-card-text>
                              <v-text-field
                                dense
                                v-model.trim.number="item.timeInMinutes"
                                :label="$t('Time')"
                                value="2"
                                :suffix="$t('Minutes')"
                                hide-details
                                type="Number"
                                :min="1"
                                autofocus
                                @input="
                                  item.timeInMinutes =
                                    $event !== '' ? $event : 1;
                                  onScheduleTimeUpdate(item);
                                "
                                @keydown="filterKey"
                                @keydown.enter="
                                  onTimeMenuEnter(
                                    `menuRef_${parentIndex}_${index}`
                                  )
                                "
                                :maxlength="timeDigitTextLimit"
                                pattern="/^-?\d+\.?\d*$/"
                                onKeyPress="if(this.value.length==maxLength) { return false; }"
                              ></v-text-field>
                            </v-card-text>
                          </v-card>
                        </v-menu>
                      </v-col>
                      <v-col
                        cols="12"
                        sm="3"
                        md="3"
                        lg="3"
                        class="agendaCol"
                        style="margin-top: 0px !important"
                      >
                        <v-list-item>
                          <v-list-item-content style="width: 200px">
                            <div
                              v-for="pic in item.peopleInCharge"
                              :key="pic.email"
                            >
                              <MeetingPresentor
                                :person="getPresentor(pic)"
                              ></MeetingPresentor>
                            </div>
                          </v-list-item-content>
                          <v-list-item-action>
                            <MeetingPresentorList
                              v-if="$security.checkUserAccess('UpdateMeeting')"
                              :items="meetingObject.attendees"
                              :selected-item="item"
                              @onSelection="onChangePresentor"
                            ></MeetingPresentorList>
                          </v-list-item-action>
                        </v-list-item>
                      </v-col>
                      <v-col cols="12" sm="3" md="3" lg="3" class="agendaCol">
                        <div
                          v-cloak
                          @drop.prevent="
                            $security.checkUserAccess('UpdateMeeting')
                              ? addDropFile($event, item)
                              : ''
                          "
                          @dragover.prevent
                          style="
                            padding-top: 15px;
                            padding-bottom: 10px;
                            min-height: 50px;
                          "
                        >
                          <v-row
                            v-if="$security.checkUserAccess('UpdateMeeting')"
                          >
                            <div>
                              <input
                                v-show="false"
                                type="file"
                                :id="item.Id"
                                class="inputfile"
                                @change="filesChange($event.target.files, item)"
                              />
                              <label
                                :for="item.Id"
                                style="padding-right: 5px; padding-left: 5px"
                              >
                                <v-icon class="mr-1" color="grey"
                                  >mdi-attachment</v-icon
                                >
                                {{ $t("agenda.AttachFile") }}
                              </label>
                            </div>
                          </v-row>
                          <v-row>
                            <div
                              v-for="doc in item.documents"
                              :key="doc.documentId"
                              :class="[
                                $security.checkUserAccess('DownloadDocument')
                                  ? 'downloadLink'
                                  : '',
                                countActiveDocuments > 0 &&
                                activeDocumentViews[doc.documentId] == true
                                  ? 'active'
                                  : '',
                              ]"
                            >
                              <div>
                                <v-tooltip
                                  bottom
                                  v-if="
                                    $security.checkUserAccess(
                                      'DownloadDocument'
                                    )
                                  "
                                >
                                  <template v-slot:activator="{ on, attrs }">
                                    <span
                                      @click="documentDownload(doc)"
                                      v-bind="attrs"
                                      v-on="on"
                                      ><v-icon size="20" left
                                        >mdi-file-document</v-icon
                                      >{{ doc.fileName }}
                                      <v-icon
                                        size="20"
                                        color="success"
                                        v-if="
                                          !activeDocumentViews[
                                            doc.documentId
                                          ] ||
                                          activeDocumentViews[doc.documentId] ==
                                            false
                                        "
                                        >mdi-new-box</v-icon
                                      >
                                    </span>
                                  </template>
                                  <p class="ma-0">
                                    File Name: {{ doc.fileName }}
                                  </p>
                                  <p class="ma-0" v-if="doc.size">
                                    Size: {{ formatBytes(doc.size) }}
                                  </p>

                                  <p class="ma-0" v-if="doc.lastModified">
                                    Last Modified Time:
                                    {{
                                      convertIntlDateFormat(doc.lastModified)
                                    }}
                                  </p>
                                </v-tooltip>
                                <span v-else
                                  ><v-icon size="20" left
                                    >mdi-file-document</v-icon
                                  >{{ doc.fileName }}</span
                                >

                                <v-tooltip bottom>
                                  <template v-slot:activator="{ on, attrs }">
                                    <v-icon
                                      v-if="
                                        $security.checkUserAccess(
                                          'DownloadDocument'
                                        )
                                      "
                                      size="20"
                                      right
                                      @click="saveDocument(doc)"
                                      v-bind="attrs"
                                      v-on="on"
                                      >mdi-download</v-icon
                                    >
                                  </template>
                                  <span>{{ $t("agenda.tipSaveDoc") }}</span>
                                </v-tooltip>
                                <v-tooltip bottom>
                                  <template v-slot:activator="{ on, attrs }">
                                    <v-icon
                                      v-if="
                                        $security.checkUserAccess(
                                          'UpdateMeeting'
                                        )
                                      "
                                      size="20"
                                      right
                                      @click="deleteDocument(doc, item)"
                                      v-bind="attrs"
                                      v-on="on"
                                      >mdi-delete</v-icon
                                    >
                                  </template>
                                  <span>{{ $t("agenda.tipDeleteDoc") }}</span>
                                </v-tooltip>
                              </div>
                            </div>
                          </v-row>
                        </div>
                      </v-col>
                      <v-col cols="12" sm="1" md="1" lg="1" class="agendaCol">
                        <v-tooltip
                          bottom
                          v-if="$security.checkUserAccess('UpdateMeeting')"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              x-small
                              v-on="on"
                              v-bind="attrs"
                              color="pageHeader"
                              text
                              @click.stop="confirmTopicRemoval(item)"
                            >
                              <v-icon small>mdi-delete</v-icon>
                              {{ $t("Remove") }}</v-btn
                            >
                          </template>
                          <span>{{ $t("agenda.tipRemoveTopic") }}</span>
                        </v-tooltip>
                      </v-col>
                    </v-row>
                  </draggable>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </draggable>
          </v-expansion-panels>
        </div>
      </v-card-text>
    </v-card>
    <div>
      <v-toolbar id="agenda" flat height="30">
        <strong class="text--secondary"> {{ $t("agenda.lblMOM") }}:</strong>
        <v-spacer></v-spacer>
      </v-toolbar>
      <v-card outlined elevation="2" min-width="1000px">
        <v-card-text>
          <div
            v-if="$security.checkUserAccess('UpdateMeeting')"
            v-cloak
            @drop.prevent="addDropMeetingMinutesFile($event)"
            @dragover.prevent
          >
            <input
              v-show="false"
              type="file"
              id="momDocument"
              class="inputMOM"
              @change="meetingMinutesChanged($event.target.files)"
            />
            <label
              for="momDocument"
              style="padding-right: 5px; padding-left: 5px"
            >
              <v-icon class="mr-1" color="grey">mdi-attachment</v-icon>
              {{ $t("agenda.AttachMOMFile") }}
            </label>
          </div>

          <v-list dense v-if="momDocuments.length > 0" class="file-list">
            <v-list-item
              dense
              v-for="(file, index) in momDocuments"
              :key="index"
              link
              class="pt-0 pb-0"
            >
              <v-list-item-avatar>
                <v-icon class="mdi-36px" color="grey lighten-1"
                  >mdi-file</v-icon
                >
              </v-list-item-avatar>
              <v-list-item-content>
                <v-tooltip
                  bottom
                  v-if="$security.checkUserAccess('DownloadDocument')"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-list-item-title
                      @click="documentDownload(file)"
                      :class="[
                        $security.checkUserAccess('DownloadDocument')
                          ? 'downloadLink'
                          : '',
                        countActiveDocuments > 0 &&
                        activeDocumentViews[file.documentId] == true
                          ? 'active'
                          : '',
                      ]"
                      v-bind="attrs"
                      v-on="on"
                      >{{ file.fileName }}
                      <v-icon
                        size="20"
                        color="success"
                        v-if="
                          !activeDocumentViews[file.documentId] ||
                          activeDocumentViews[file.documentId] == false
                        "
                        >mdi-new-box</v-icon
                      >
                    </v-list-item-title>
                  </template>
                  <span>{{ $t("agenda.tipOpenDoc") }}</span>
                </v-tooltip>
                <v-list-item-title v-else
                  >{{ file.fileName }}
                  <v-icon
                    size="20"
                    color="success"
                    v-if="
                      !activeDocumentViews[file.documentId] ||
                      activeDocumentViews[file.documentId] == false
                    "
                    >mdi-new-box</v-icon
                  ></v-list-item-title
                >
                <v-list-item-subtitle
                  >{{ formatBytes(file.size) }} -
                  {{ file.mimetype }}</v-list-item-subtitle
                >
              </v-list-item-content>
              <v-list-item-action>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      v-if="$security.checkUserAccess('UpdateMeeting')"
                      size="20"
                      right
                      @click="deleteMOMDocument(file)"
                      v-bind="attrs"
                      v-on="on"
                      >mdi-delete</v-icon
                    >
                  </template>
                  <span>{{ $t("agenda.tipDeleteDoc") }}</span>
                </v-tooltip>
              </v-list-item-action>
            </v-list-item>
          </v-list>
          <div v-else>
            <span>{{ $t("agenda.noMeetingMinutesDocument") }}</span>
          </div>
          <!-- <div
              v-for="doc in momDocuments"
              :key="doc.documentId"
              :class="
                $security.checkUserAccess('DownloadDocument')
                  ? 'downloadLink'
                  : ''
              "
            >
              <div>
                <v-tooltip
                  bottom
                  v-if="$security.checkUserAccess('DownloadDocument')"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <span
                      @click="documentDownload(doc)"
                      v-bind="attrs"
                      v-on="on"
                      ><v-icon size="20" left>mdi-file-document</v-icon
                      >{{ doc.fileName }}</span
                    >
                  </template>
                  <span>{{ $t("agenda.tipOpenDoc") }}</span>
                </v-tooltip>
                <span v-else
                  ><v-icon size="20" left>mdi-file-document</v-icon
                  >{{ doc.fileName }}</span
                >
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      v-if="$security.checkUserAccess('UpdateMeeting')"
                      size="20"
                      right
                      @click="deleteMOMDocument(doc)"
                      v-bind="attrs"
                      v-on="on"
                      >mdi-delete</v-icon
                    >
                  </template>
                  <span>{{ $t("agenda.tipDeleteDoc") }}</span>
                </v-tooltip>
              </div>
            </div> -->
        </v-card-text>
      </v-card>
    </div>
    <confirm-dialog ref="confirm"></confirm-dialog>
    <MessageSheet
      :showMessage="displayMessage"
      :message="message"
      :messageType="messageType"
      @close="displayMessage = false"
    ></MessageSheet>
    <AgendaPage
      :agendaObject="agendaFormData"
      :hasCalledFromMeeting="true"
      v-if="dialogAgenda"
      v-model="dialogAgenda"
      @alert="onAlertMessage"
      @onCreateAgenda="createMeetingAgenda"
    ></AgendaPage>
    <Loader :loading="Loaderloading" :message="LoaderText"></Loader>
  </div>
</template>
<script>
import MeetingPresentor from "@/components/agenda/MeetingPresentor.vue";
import coloredChipTemplate from "@/components/agenda/coloredChipTemplate.vue";
import { mapState, mapActions, mapGetters } from "vuex";
import MeetingParticipantList from "@/components/agenda/MeetingParticipantList";
import MeetingPresentorList from "@/components/agenda/PresentorDropdownList";
import ConfirmDialog from "../ConfirmDialog.vue";
import MessageSheet from "@/components/MessageSheet.vue";
import { createUUID } from "../../utils/util";
import _ from "lodash";
import AgendaPage from "@/components/agenda/agendaPage.vue";
import DataTableRowHandler from "@/components/DataTableRowHandler.vue";
import draggable from "vuedraggable";
import AgendaTopicSelector from "@/components/agenda/agendaTopicSelector.vue";
import {
  formatBytes,
  convertIntlDateFormat,
  currentDateTime,
  openDocument,
} from "../../utils/util";
import { MAX_UPLOAD_SIZE, allowed_file } from "../../utils/constant";
import ApprovalControl from "./approvalControl.vue";
import axios from "axios";
import { timeDigitTextLimit } from "../../utils/constant";
import Loader from "@/components/Loader.vue";
import { UserAgentApplication } from "msal";
export default {
  name: "AgendaTable",
  props: {
    selectedMeeting: Object,
    topicVotes: Array,
  },
  data: function () {
    return {
      meetingClick: 0,
      meetingPopup: {},
      approvalStatus: false,
      data: {
        data: {},
        dialogMeeting: false,
        topicCategories: [
          // { order: 1, name: "Items For Approval", value: "A" },
          // { order: 2, name: "Items For Discussion", value: "S" },
          // { order: 3, name: "Regular Reporting Items", value: "R" },
        ],
      },
      timeDigitTextLimit: timeDigitTextLimit,
      popupBlocker: false,
      isExpanded: false,
      Loaderloading: false,

      // panel: [0, 1, 2, 3],
      dialogPresentor: false,
      selectedParticipantList: [],
      displayMessage: false,
      message: "",
      messageType: "",
      agendaFormData: {
        id: "",
        agendaName: "",
        category: "",
        meetingType: "",
        priority: "",
        frequency: "",
        resolutionType: "",
        topics: [],
        tenantid: sessionStorage.getItem("tenantId"),
        memo: "",
      },
      dialogAgenda: false,
      newAgendaObjectList: [],
      catTopics: [],
      momDocs: [],
      activeDocument: [],
      userVotingData: [],
      alertnateContactEmails: [],
    };
  },

  components: {
    MeetingPresentor,
    coloredChipTemplate,
    MeetingParticipantList,
    MeetingPresentorList,
    ConfirmDialog,
    MessageSheet,
    AgendaPage,
    DataTableRowHandler,
    draggable,
    AgendaTopicSelector,
    ApprovalControl,
    Loader,
  },
  async created() {
    await this.getTenantConfigList();
    this.detectPopup();

    this.data.topicCategories = this.tenantSettingTypes("topiccategory")
      .map((item) => ({
        ...item,
        order: this.getTopicOrder(item),
      }))
      .sort((a, b) => a.order - b.order);

    // this.data.topicCategories = this.tenantSettingTypes("topiccategory").sort(
    //   (a, b) => a.order - b.order
    // );
  },
  async mounted() {
    if (
      this.selectedMeeting.id &&
      this.$security.checkUserAccess("VotingRight")
    ) {
      const params = {
        meetingId: this.selectedMeeting.id,
      };
      const response = await this.getUserMeetingVotes(params);
      if (response) {
        this.userVotingData = response;
      }
      this.approvalStatus = true;
    }

    if (this.selectedMeeting.id && this.selectedMeeting.attendees.length > 0) {
      let alternateEmailsIds = await this.getAlternateEmailIds();
      this.alertnateContactEmails = this.selectedMeeting.attendees.filter(
        function (person) {
          return alternateEmailsIds.includes(person.email);
        }
      );
    }
  },
  computed: {
    LoaderText() {
      return this.$t("LoaderText");
    },
    selectedLang() {
      return this.$i18n.locale;
    },
    hasResolutionInWriting() {
      if (this.meetingObject.resolutionType === "In Writing") {
        return true;
      }
      return false;
    },
    isAttendee() {
      let attendee = this.meetingObject.attendees.filter(
        (f) =>
          f.email.toLowerCase().trim() ===
          this.$auth.user.email.toLowerCase().trim()
      );
      if (attendee.length > 0) {
        return true;
      }

      if (this.alertnateContactEmails.length > 0) {
        return true;
      }
      return false;
    },
    selectedTenant() {
      return sessionStorage.getItem("tenantId");
    },
    ...mapState({
      agendaListData: (state) => state.agenda.agendaList,
      documentViews: (state) => state.document.documentViews,
    }),
    ...gettersMap(),
    panel: {
      get() {
        return this.data.topicCategories.map((element, index) => {
          return index;
        });
      },
      set(val) {},
    },
    activeDocumentViews() {
      return { ...this.documentViews, ...this.activeDocument };
    },
    countActiveDocuments() {
      return Object.keys(this.activeDocumentViews).length;
    },
    meetingObject: {
      get() {
        return this.selectedMeeting;
      },
      set(val) {
        this.selectedMeeting = val;
      },
    },

    momDocuments() {
      this.momDocs = this.meetingObject.momDocuments
        ? this.meetingObject.momDocuments
        : [];
      return this.momDocs;
    },
    topics() {
      if (!this.meetingObject.topics) {
        return [];
      }
      let data = this.meetingObject.topics.map((item) => ({
        ...item,
        agenda: this.agendaListData.filter((f) => f.id === item.agendaId)[0]
          ? this.agendaListData.filter((f) => f.id === item.agendaId)[0]
          : this.newAgendaObjectList.filter((f) => f.id === item.agendaId)[0],
      }));

      return data;
    },
    messageDownloadURL() {
      return (
        process.env.VUE_APP_CONVOCATION_SERVICE +
        "convocation/" +
        this.meetingObject.id +
        "?tenant=" +
        this.selectedTenant
      );
    },
    categorizedTopics: {
      get() {
        this.catTopics = this.data.topicCategories.map((item) => ({
          ...item,
          topics: this.topics
            .filter((s) => s.topicCategory === item.value)
            .sort((a, b) => a.displayOrder - b.displayOrder),
        }));

        return this.catTopics;
      },
      set(val) {
        this.catTopics = val;
      },
    },
    topicHeaders() {
      return [
        {
          align: " d-none",
          text: "Agenda Title",
          value: "agenda.agendaName",
          sortable: false,
        },
        {
          text: "Agenda & Topics",
          align: "start",
          value: "topicName",
          sortable: false,
          groupable: false,
        },

        {
          align: "start",
          text: "Category",
          value: "topicCategory",
          groupable: false,
          sortable: false,
        },
        {
          align: "start",
          text: "Priority",
          value: "agenda.priority",
          width: 80,
          groupable: false,
          sortable: false,
        },
        {
          align: "start",
          text: "Scheduled Time",
          value: "timeInMinutes",
          width: 80,
          groupable: false,
          sortable: false,
        },
        {
          align: " d-none",
          text: "Actual Time",
          value: "actualTime",
          class: "appBar",
          sortable: false,
          groupable: false,
        },
        {
          align: "start",
          text: "Presentor",
          value: "peopleInCharge",
          class: "presentorHeader",
          sortable: false,
          groupable: false,
        },
        {
          align: "start",
          text: "Documents",
          value: "documents",
          groupable: false,
          sortable: false,
        },
      ];
    },
    topicHeads() {
      return [
        {
          text: "Agenda & Topics",
          align: "start",
          value: "topicName",
          sortable: false,
          groupable: false,
          width: "31%",
        },
        {
          align: "start",
          text: "Priority",
          value: "agenda.priority",
          width: 80,
          groupable: false,
          sortable: false,
        },
        {
          align: "start",
          text: "Scheduled Time",
          value: "timeInMinutes",
          width: 80,
          groupable: false,
          sortable: false,
        },

        {
          align: "start",
          text: "Presentor",
          value: "peopleInCharge",
          class: "presentorHeader",
          sortable: false,
          groupable: false,
        },
        {
          align: "start",
          text: "Documents",
          value: "documents",
          groupable: false,
          sortable: false,
        },
      ];
    },
  },
  methods: {
    async getAlternateEmailIds() {
      let contactEmailIds = [];
      // const attendeesList = this.selectedMeeting.attendees;
      // const attendees = attendeesList.find(
      //   (f) =>
      //     String(f.email).toLowerCase().trim() ===
      //     String(this.$auth.user.email).toLowerCase().trim()
      // );
      await this.getUserByEmailId(this.$auth.user.email.toLowerCase().trim())
        .then((response) => {
          let user = response;
          if (user.contactEmailIds) {
            contactEmailIds = user.contactEmailIds;
          }
        })
        .catch((error) => {});
      // if (attendees) {
      //   contactEmailIds = compact(attendees.contactEmailIds);
      // }

      return contactEmailIds;
    },
    getVote(topicid) {
      return this.userVotingData.find((s) => s.topicId === topicid);
    },
    getTopicOrder(item) {
      if (!this.meetingObject.topicCategories) {
        return item.order;
      }

      let cat = this.meetingObject.topicCategories.find(
        (f) => f.value === item.value
      );
      if (cat) {
        return cat.order;
      }

      return item.order;
    },
    formatBytes,
    convertIntlDateFormat,
    currentDateTime,
    ...mapActions({
      getUserMeetingVotes: "meeting/getUserMeetingVotes",
      deleteDocuments: "document/deleteDocuments",
      createDocuments: "document/createDocuments",
      updateMeetingData: "meeting/updateMeeting",
      getMeetingDetails: "meeting/getMeetingById",
      getDocumentsByMeetingId: "document/getDocumentsByMeetingId",
      getConvocation: "meeting/getConvocationById",
      getTenantConfigList: "tenant/getTenantConfigList",
      downloadDocumentAPI: "document/downloadDocument",
      downloadConvocationMessageAPI: "meeting/downloadConvocationMessage",
      addDocumentView: "document/addDocumentView",
      getUserByEmailId: "admin/getUserByEmailId",
    }),
    onScheduleTimeUpdate(topic) {
      let meetTopic = this.meetingObject.topics.find(
        (s) => String(s.Id).toLowerCase() === String(topic.Id).toLowerCase()
      );
      if (
        meetTopic &&
        String(topic.timeInMinutes).length < this.timeDigitTextLimit
      ) {
        meetTopic.timeInMinutes =
          topic.timeInMinutes !== "" ? topic.timeInMinutes : 1;
        this.$eventHub.$emit("onMeetingUpdate");
      }
    },
    showParticipantChangeDialog(participantlist) {
      this.selectedParticipantList = participantlist;
      this.dialogPresentor = true;
    },
    closeAlert() {
      this.popupBlocker = false;
    },
    hasTopicsForCategory(cat) {
      if (this.getTopicArray(cat).length > 0) return true;
      return false;
    },
    getTopicArray(cat) {
      return this.topics.filter((s) => s.topicCategory === cat);
    },
    onAlertMessage: function (message, MessageType) {
      this.showMessageSheet(message, MessageType);
    },
    async onApprovalChange(topic) {
      if (
        this.selectedMeeting.id &&
        this.$security.checkUserAccess("VotingRight")
      ) {
        const params = {
          meetingId: this.selectedMeeting.id,
        };
        const response = await this.getUserMeetingVotes(params);
        if (response) {
          this.userVotingData = response;
        }
      }
    },
    onApprovalSettingChange(topic) {
      this.$eventHub.$emit("onMeetingUpdate");
      this.$eventHub.$emit("saveOnApproval", topic);
    },
    onChangePresentor(topic) {
      let meetTopic = this.meetingObject.topics.find(
        (s) => String(s.Id).toLowerCase() === String(topic.Id).toLowerCase()
      );
      if (meetTopic) {
        meetTopic.peopleInCharge = topic.peopleInCharge;

        this.$eventHub.$emit("onMeetingUpdate");
      }
    },

    handleTopicSelection(meeting) {
      this.$eventHub.$emit("onMeetingUpdate");
    },
    createMeetingAgenda(agendaObject) {
      agendaObject.meetingType = this.meetingObject.meetingType;
      this.newAgendaObjectList.push({ ...agendaObject });
      this.$eventHub.$emit("onNewAgenda", this.newAgendaObjectList);
      agendaObject.topics.forEach((topic) => {
        this.meetingObject.topics.push({ ...topic });
      });
    },

    showMessageSheet(message, messageType) {
      this.displayMessage = true;
      this.message = message;
      this.messageType = messageType;
    },

    addDropFile(e, item) {
      this.filesChange(e.dataTransfer.files, item);
    },
    filesChange(fileList, rowData) {
      const maxSize = MAX_UPLOAD_SIZE ?? 0;
      const documents = [];
      const filesWithStatus = [];
      for (let item of fileList) {
        if (!allowed_file.includes(item.type)) {
          filesWithStatus.push(
            this.$t("fileErrorMsg", { filename: item.name })
          );
        } else if (item.size > maxSize) {
          filesWithStatus.push(
            this.$t("fileSizeErrorMsg", { maxsize: formatBytes(maxSize) })
          );
        } else {
          documents.push(item);
        }
      }
      if (filesWithStatus.length > 0) {
        this.showMessageSheet(filesWithStatus.join("/n"), "error");
      }
      let formData;
      Array.from(Array(documents.length).keys()).map(async (x) => {
        const newDocObj = {
          documentId: null,
          tenantid: this.selectedTenant,
          topicId: rowData.Id,
          meetingid: this.meetingObject.id,
          agendaid: rowData.agendaId,
          directoryid: "",
          peopleid: "",
          fileName: "",
          mimetype: "",
          formData: null,
          action: "add",
        };

        let documentId = createUUID();
        formData = new FormData();
        formData.append("tenantId", this.selectedTenant);
        formData.append("topicId", rowData.Id);
        formData.append("documentId", documentId);
        formData.append("meetingId", this.meetingObject.id);
        formData.append("agendaId", rowData.agendaId);
        formData.append("directoryId", "");
        formData.append("peopleId", "");
        formData.append("attachment", documents[x]);

        newDocObj.formData = formData;
        newDocObj.documentId = documentId;
        newDocObj.fileName = documents[x].name;
        newDocObj.mimetype = documents[x].type;
        newDocObj.size = documents[x].size;
        newDocObj.lastModified = currentDateTime(documents[x].lastModifiedDate);
        rowData.documents.push(newDocObj);
      });
      this.$eventHub.$emit("onMeetingUpdate");
    },

    addDropMeetingMinutesFile(e) {
      this.meetingMinutesChanged(e.dataTransfer.files);
    },
    meetingMinutesChanged(fileList) {
      let formData;
      Array.from(Array(fileList.length).keys()).map(async (x) => {
        const newDocObj = {
          documentId: null,
          tenantid: this.selectedTenant,
          topicId: "",
          meetingid: this.meetingObject.id,
          agendaid: "",
          directoryid: "",
          peopleid: "",
          fileName: "",
          mimetype: "",
          size: "",
          lastModified: "",
          formData: null,
          action: "add",
        };

        let documentId = createUUID();
        formData = new FormData();
        formData.append("tenantId", this.selectedTenant);
        formData.append("topicId", "");
        formData.append("documentId", documentId);
        formData.append("meetingId", this.meetingObject.id);
        formData.append("agendaId", "");
        formData.append("directoryId", "");
        formData.append("peopleId", "");
        formData.append("attachment", fileList[x]);

        newDocObj.formData = formData;

        newDocObj.documentId = documentId;
        newDocObj.fileName = fileList[x].name;
        newDocObj.mimetype = fileList[x].type;
        newDocObj.size = fileList[x].size;
        (newDocObj.lastModified = currentDateTime(fileList[x].lastModified)),
          this.momDocs.push(newDocObj);
        this.meetingObject.momDocuments = this.momDocs;
      });
      this.$eventHub.$emit("onMeetingUpdate");
    },
    async documentDownload(item) {
      if (item.action === "add") {
        this.showMessageSheet(this.$t("agenda.CannotOpenDocInfo"), "info");
      } else {
        const params = {
          documentId: item.documentId,
          filename: item.fileName,
        };
        const res = await this.downloadDocumentAPI(params);
        var contentType = res.headers["content-type"];
        var blob = new Blob([res.data], { type: contentType });
        var url = window.URL.createObjectURL(blob);
        this.activeDocument = {
          ...this.activeDocument,
          ...{ [item.documentId]: true },
        };

        this.addDocumentView(item.documentId);
        openDocument(url, item.fileName);
      }
    },

    async saveDocument(item) {
      if (item.action === "add") {
        this.showMessageSheet(this.$t("agenda.CannotSaveDocInfo"), "info");
      } else {
        const params = {
          documentId: item.documentId,
          filename: item.fileName,
        };
        const res = await this.downloadDocumentAPI(params);
        var contentType = res.headers["content-type"];
        var blob = new Blob([res.data], { type: contentType });
        var url = window.URL.createObjectURL(blob);
        this.activeDocument = {
          ...this.activeDocument,
          ...{ [item.documentId]: true },
        };

        this.addDocumentView(item.documentId);
        //openDocument(url, item.fileName);
        this.downloadMessage(url, item.fileName);
      }
    },

    detectPopup() {
      var popupWindow = window.open(
        "about:blank",
        "new_window_123",
        "height=1,width=1"
      );

      if (!popupWindow) {
        //alert("popup will be blocked");
        this.popupBlocker = true;
      } else {
        // alert("popup will be shown");
        this.popupBlocker = false;
        //window.open("", "_self");
        popupWindow.close();
      }
    },
    openMeetingTimer() {
      this.meetingClick = this.meetingClick + 1;
      var height = screen.height;
      var width = screen.width;
      var strWindowFeatures =
        "location=no,toolbar=no,menubar=no,height=" +
        height +
        ",width=" +
        width +
        ",scrollbars=auto,fullscreen=yes";

      let popup;
      let routeData = this.$router.resolve({
        path: "/meetingTimer/" + this.meetingObject.id,
      });

      if (
        this.meetingClick === 1 ||
        typeof this.meetingPopup === "undefined" ||
        this.meetingPopup.closed
      ) {
        popup = window.open(routeData.href, "_blank", strWindowFeatures);
        this.meetingPopup = popup;
        popup.moveTo(0, 0);
      } else {
        this.meetingPopup.location = routeData.href;
        this.meetingPopup.focus();
      }
    },
    createAgenda() {
      this.agendaFormData = {
        id: "",
        agendaName: "",
        category: "",
        meetingType: this.meetingTypesList.find(
          (c) =>
            String(c.label).toLowerCase() ===
            String(this.meetingObject.meetingType).toLowerCase()
        ) || { label: this.meetingObject.meetingType, color: "#F5CBA7" },
        priority: "",
        frequency: "",
        resolutionType: "",
        topics: [],
        tenantid: this.selectedTenant,
        memo: "",
      };
      this.dialogAgenda = true;
    },

    editAgenda(agendaObj) {
      this.agendaFormData = Object.assign({}, agendaObj);

      this.dialogAgenda = true;
    },

    async OpenAllDocuments() {
      if (this.popupBlocker) {
        await this.popupBlockerAlert();
        return;
      }
      this.Loaderloading = true;
      let isDocumentExist = false;

      let promises = [];
      let receivedData;
      this.categorizedTopics.forEach((category) => {
        if (category.topics.length > 0) {
          category.topics.forEach((topic) => {
            if (topic.documents.length > 0) {
              isDocumentExist = true;
              topic.documents.map((x) => {
                this.activeDocument = {
                  ...this.activeDocument,
                  ...{ [x.documentId]: true },
                };
                const params = {
                  documentId: x.documentId,
                  filename: x.fileName,
                };
                promises.push(this.downloadDocumentAPI(params));
              });
            }
          });
        }
      });
      receivedData = await Promise.all(promises);
      receivedData.map(async (x) => await this.handleDownloadResponse(x));
      this.Loaderloading = false;
      if (!isDocumentExist) {
        this.showMessageSheet(this.$t("agenda.NoDocAttached"), "info");
      }
    },

    async SaveAllDocuments() {
      this.Loaderloading = true;
      let isDocumentExist = false;

      let promises = [];
      let receivedData;
      this.categorizedTopics.forEach((category) => {
        if (category.topics.length > 0) {
          category.topics.forEach((topic) => {
            if (topic.documents.length > 0) {
              isDocumentExist = true;
              topic.documents.map((x) => {
                this.activeDocument = {
                  ...this.activeDocument,
                  ...{ [x.documentId]: true },
                };
                const params = {
                  documentId: x.documentId,
                  filename: x.fileName,
                };
                promises.push(this.downloadDocumentAPI(params));
              });
            }
          });
        }
      });
      receivedData = await Promise.all(promises);
      receivedData.map(async (x) => await this.handleDownloadResponse(x, true));
      this.Loaderloading = false;
      if (!isDocumentExist) {
        this.showMessageSheet(this.$t("agenda.NoDocAttached"), "info");
      }
    },

    async handleDownloadResponse(res, isSave = false) {
      var fileName = res.config.fileName;
      var contentType = res.headers["content-type"];
      var blob = new Blob([res.data], { type: contentType });
      var url = window.URL.createObjectURL(blob);
      if (isSave) {
        this.downloadMessage(url, fileName);
      } else {
        openDocument(url, fileName);
      }
    },
    async downloadConvocation() {
      //const convocationStream = await this.getConvocation(this.meetingObject.id);
      //var sampleBytes = this.fromBinary(convocationStream);
      const res = await this.downloadConvocationMessageAPI(
        this.meetingObject.id
      );
      var contentType = res.headers["content-type"];
      var blob = new Blob([res.data], { type: contentType });
      var url = window.URL.createObjectURL(blob);
      this.downloadMessage(url, [this.meetingObject.meetingName + ".msg"]);

      this.showMessageSheet(this.$t("agenda.ConvoEmailDownloaded"), "success");
    },

    downloadMessage(url, name) {
      var a = document.createElement("a");
      document.body.appendChild(a);
      a.style = "display: none";
      a.href = url;
      a.download = name;
      a.click();
      window.URL.revokeObjectURL(url);
    },
    base64ToArrayBuffer(base64) {
      //var binaryString =  window.atob(base64);

      var binaryLen = binaryString.length;
      var bytes = new Uint8Array(binaryLen);
      for (var i = 0; i < binaryLen; i++) {
        var ascii = binaryString.charCodeAt(i);
        bytes[i] = ascii;
      }
      return bytes;
    },

    customSort(items, index, isDesc) {
      items.sort((a, b) => {
        if (index == "topicCategory") {
          let x = this.getTopicCategoryOrder(a[index]);
          let y = this.getTopicCategoryOrder(b[index]);
          if (!isDesc) {
            return x > y ? -1 : 1;
          } else {
            return y > x ? -1 : 1;
          }
        } else {
          if (!isDesc) {
            return a[index] < b[index] ? -1 : 1;
          } else {
            return b[index] < a[index] ? -1 : 1;
          }
        }
      });
      return items;
    },
    getDay(meetingdate) {
      var Xmas95 = new Date(meetingdate);
      //alert(this.selectedLang);
      var options = { dateStyle: "full", timeStyle: "short" };
      return new Intl.DateTimeFormat(this.selectedLang, options).format(Xmas95);
      return Xmas95;
    },
    getTopicCategoryText(cat) {
      //let categoryVal = cat.charAt(0);
      return this.data.topicCategories.find((v) => v.value === cat).name;
    },
    getTopicCategoryOrder(cat) {
      //let categoryVal = cat.charAt(0);
      return this.data.topicCategories.find((v) => v.value === cat).order;
    },
    getPresentor(picID) {
      let pic = this.meetingObject.attendees.find(
        (a) => String(a.id).toLowerCase() === String(picID).toLowerCase().trim()
      );
      if (pic) {
        return pic;
      }
      return null;
    },
    async confirmTopicRemoval(item) {
      if (
        await this.$refs.confirm.open(
          this.$t("lblConfirmRemoval"),
          this.$t("msgConfirmRemoval") + " " + item.topicName + "?",
          { yesNoButtons: true }
        )
      ) {
        this.removeTopic(item);
      }
    },
    removeTopic(item) {
      if (item.documents.length > 0) {
        item.documents.forEach((doc) => {
          this.$eventHub.$emit("onDeleteDocuments", doc.documentId);
        });
      }
      this.meetingObject.topics = this.meetingObject.topics.filter(
        (f) => f.Id != item.Id
      );
      this.$eventHub.$emit("onMeetingUpdate");
    },

    groupBy(array, key) {
      // Return the end result
      return array.reduce((result, currentValue) => {
        // If an array already present for key, push it to the array. Else create an array and push the object
        (result[currentValue[key]] = result[currentValue[key]] || []).push(
          currentValue
        );
        // Return the current iteration `result` value, this will be taken as next iteration `result` value and accumulate
        return result;
      }, {}); // empty object is the initial value for result object
    },
    groupByKey(array, key) {
      return array.reduce((hash, obj) => {
        if (obj[key] === undefined) return hash;
        return Object.assign(hash, {
          [obj[key]]: (hash[obj[key]] || []).concat(obj),
        });
      }, {});
    },
    onChanged(evt, category) {
      for (var i = evt.moved.newIndex; i < category.topics.length; i++) {
        let topic = category.topics[i];
        topic.displayOrder = i;
        let metTopic = this.meetingObject.topics.find((f) => f.Id === topic.Id);
        if (metTopic) {
          metTopic.displayOrder = i;
        }
      }
      this.$eventHub.$emit("onMeetingUpdate");
    },

    onCatOrderChanged(evt) {
      for (
        var i = evt.moved.newIndex;
        i < this.data.topicCategories.length;
        i++
      ) {
        let topic = this.data.topicCategories[i];
        topic.order = i;
      }
      this.meetingObject.topicCategories = this.data.topicCategories;
      this.$eventHub.$emit("onMeetingUpdate");
    },
    checkAccessPermission(evt) {
      evt.preventDefault();
    },
    filterKey(e) {
      const key = e.key;
      if (key === ".") return e.preventDefault();
      if (key === "e") return e.preventDefault();
      if (key === "-") return e.preventDefault();
    },
    onTimeMenuEnter(ref) {
      this.$refs[ref][0].isActive = false;
    },
    deleteDocument,
    deleteMOMDocument,
    ConfirmDelete,
    alertDialog,
    createUUID,
    updateMeeting,
    popupBlockerAlert,
    getUniqueId,
  },
};

function getUniqueId() {
  return `_${Math.random().toString(36).substr(2, 9)}`;
}

async function updateMeeting() {
  const updatedMeetingObj = await this.getMeetingDetails(this.meetingObject.id);

  updatedMeetingObj.topics = this.meetingObject.topics;

  this.updateMeetingData(updatedMeetingObj).then((response) => {});
}

async function deleteDocument(item, rowData) {
  const answer = await this.ConfirmDelete();
  if (answer) {
    if (item.documentId) {
      for (var i = 0; i < rowData.documents.length; i++) {
        var obj = rowData.documents[i];
        if (item.documentId === obj.documentId) {
          rowData.documents.splice(i, 1);
          i--;
        }
      }
      this.$eventHub.$emit("onMeetingUpdate");
      this.$eventHub.$emit("onDeleteDocuments", item.documentId);
    }
  }
}

async function deleteMOMDocument(item) {
  const answer = await this.ConfirmDelete();
  if (answer) {
    if (item.documentId) {
      for (var i = 0; i < this.momDocuments.length; i++) {
        var obj = this.momDocuments[i];
        if (item.documentId === obj.documentId) {
          this.momDocuments.splice(i, 1);
          i--;
        }
      }
      this.$eventHub.$emit("onMeetingUpdate");
      this.$eventHub.$emit("onDeleteDocuments", item.documentId);
    }
  }
}

async function ConfirmDelete() {
  if (
    await this.$refs.confirm.open(
      this.$t("agenda.hDeleteMessage"),
      this.$t("agenda.lblDeleteMessage")
    )
  ) {
    return true;
  }
}

async function popupBlockerAlert() {
  if (
    await this.$refs.confirm.open(
      this.$t("agenda.lblPopupBlocked"),
      this.$t("agenda.msgPopupBlocked"),
      { noconfirm: true, dialogIcon: "alert" }
    )
  ) {
    return true;
  }
}
async function alertDialog() {
  if (
    await this.$refs.confirm.open(
      this.$t("Alert"),
      this.$t("agenda.CannotDownloadAlert"),
      { noconfirm: true }
    )
  ) {
    return true;
  }
}

function gettersMap() {
  return mapGetters({
    // categoriesList: "tenant/agendaCategories",
    meetingTypesList: "tenant/agendaMeetingTypes",
    tenantSettingTypes: "tenant/tenantSettingTypes",
  });
}

var downloadMsg = (function () {
  var a = document.createElement("a");
  document.body.appendChild(a);
  a.style = "display: none";

  return function (data, name) {
    var blob = new Blob(data, {
        type: "application/octet-stream;charset=utf-8",
      }),
      url = window.URL.createObjectURL(blob);
    a.href = url;
    a.download = name;
    a.click();
    window.URL.revokeObjectURL(url);
  };
})();
</script>

<style scoped>
.presentorHeader {
  text-align: center !important;
}
::v-deep .v-toolbar__content,
.v-toolbar__extension {
  padding: 4px 0px;
}

::v-deep .v-sheet.v-card:not(.v-sheet--outlined) {
  box-shadow: none !important;
}

::v-deep .v-expansion-panel-content__wrap {
  padding: 0px;
}

::v-deep .inputfile + label {
  border: dashed 1px lightgray;
  border-radius: 3;
  color: darkgray;
  display: inline-block;
  min-width: 250px;
}

::v-deep .inputfile:focus + label,
.inputfile + label:hover {
  background-color: var(--v-warning-lighten4);
  cursor: pointer; /* "hand" cursor */
}
::v-deep .downloadLink {
  display: block;
  width: 100%;
}
::v-deep .downloadLink:hover {
  color: var(--v-primary-darken1);
  cursor: pointer; /* "hand" cursor */
}

::v-deep .downloadLink.active {
  color: var(--v-primary-darken1);
}

::v-deep .listItem {
  unicode-bidi: isolate !important;
  font-variant-numeric: tabular-nums !important;
  text-transform: none;
  text-indent: 0px !important;
  text-align: start !important;
  text-align-last: start !important;
}

::v-deep .agendaCol {
  padding-top: 2px;
  padding-bottom: 2px;
  padding-left: 5px;
  padding-right: 5px;
  margin-top: 10px;
}
::v-deep .agendaColHeader {
  padding-top: 2px;
  padding-left: 5px;
  padding-right: 5px;
}
::v-deep.agendaRow {
  border-bottom: 1px solid lightgray;
  margin-left: 10px;
}
::v-deep div.agendaRow:last-child {
  border-bottom: none;
}

::v-deep .categoryHeader {
  border: 1px solid var(--v-secondary-base) !important;
  border-radius: 5px !important;
  min-height: 30px !important;
  height: 30px !important;
  margin-bottom: 10px !important;
}

::v-deep .meetingMinutesHeader {
  border: 1px solid var(--v-appMenu-lighten3) !important;
  min-height: 30px !important;
  height: 30px !important;
}

::v-deep .inputMOM + label {
  border: dashed 1px var(--v-appMenu-lighten3);
  border-radius: 3;
  color: darkgray;
  background-color: var(--v-appBarT-base);
  display: inline-block;
  min-width: 100%;
}

::v-deep .inputMOM:focus + label,
.inputMOM + label:hover {
  background-color: var(--v-error-lighten5);
  cursor: pointer; /* "hand" cursor */
}
</style>